import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import config from '../config/config';
import { useAuth } from '../context/AuthContext';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  Paper,
  CircularProgress
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { MuiTelInput } from 'mui-tel-input';
import { FormField, filledInputSx, arborGreen } from '../components/FormDialog';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

function Register() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setToken } = useAuth();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${config.API_URL}/auth/register`, {
        email: formData.email,
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone_number: formData.phone,
        password: formData.password
      });
      // Auto-login: Set token and redirect
      setToken(response.data.access_token);
      navigate('/');
    } catch (error) {
      console.error(error);
      if (error.response?.data?.detail) {
        if (error.response.data.detail === 'Email already registered') {
          setError('This email is already registered, please try another email.');
        } else {
          setError(error.response.data.detail);
        }
      } else {
        setError('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs" sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Paper 
        elevation={0} 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          p: { xs: 4, sm: 6 }, 
          width: '100%', 
          borderRadius: 4, 
          boxShadow: '0 40px 100px rgba(10, 42, 33, 0.08)',
          border: 'none'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <AccountBalanceWalletIcon sx={{ fontSize: 40, color: arborGreen, mr: 1.5 }} />
          <Typography component="h1" variant="h4" sx={{ fontWeight: 800, color: arborGreen, letterSpacing: '-0.03em' }}>
            {t('Register')}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ mb: 4, color: 'text.secondary', textAlign: 'center', fontWeight: 500 }}>
          Create an account to start managing your books with precision.
        </Typography>
        
        {error && <Alert severity="error" sx={{ mb: 3, width: '100%', borderRadius: 2 }}>{error}</Alert>}
        
        <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
          <Stack spacing={2.5}>
            <FormField label={t('Email Address')} id="email" required>
              <TextField
                fullWidth
                id="email"
                name="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                variant="filled"
                sx={filledInputSx}
                InputProps={{ disableUnderline: true }}
                placeholder="email@example.com"
              />
            </FormField>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormField label={t('First Name')} id="firstName" required sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  id="firstName"
                  name="firstName"
                  autoComplete="given-name"
                  value={formData.firstName}
                  onChange={handleChange}
                  variant="filled"
                  sx={filledInputSx}
                  InputProps={{ disableUnderline: true }}
                  placeholder="John"
                />
              </FormField>
              <FormField label={t('Last Name')} id="lastName" required sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  id="lastName"
                  name="lastName"
                  autoComplete="family-name"
                  value={formData.lastName}
                  onChange={handleChange}
                  variant="filled"
                  sx={filledInputSx}
                  InputProps={{ disableUnderline: true }}
                  placeholder="Doe"
                />
              </FormField>
            </Box>

            <FormField label={t('Phone Number')} id="phone">
              <MuiTelInput
                fullWidth
                id="phone"
                value={formData.phone}
                onChange={(value) => setFormData({ ...formData, phone: value })}
                defaultCountry="US"
                preferredCountries={['US', 'CA']}
                variant="filled"
                sx={filledInputSx}
                InputProps={{ disableUnderline: true }}
              />
            </FormField>

            <FormField label={t('Password')} id="password" required>
              <TextField
                fullWidth
                name="password"
                type="password"
                id="password"
                autoComplete="new-password"
                value={formData.password}
                onChange={handleChange}
                variant="filled"
                sx={filledInputSx}
                InputProps={{ disableUnderline: true }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSubmit(e);
                  }
                }}
              />
            </FormField>
          </Stack>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disableElevation
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{ 
              mt: 5, 
              mb: 3, 
              py: 2, 
              fontSize: '1rem', 
              fontWeight: 700,
              bgcolor: arborGreen,
              color: '#ffffff',
              '&:hover': { bgcolor: '#061a15' },
              '&.Mui-disabled': {
                bgcolor: arborGreen,
                color: '#ffffff',
                opacity: 0.85,
              }
            }}
          >
            {loading ? 'Creating account...' : t('Create Account')}
          </Button>
          
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Link component={RouterLink} to="/" variant="body2" sx={{ color: arborGreen, fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
              {t("Already have an account? Sign in")}
            </Link>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}

export default Register;